import React, { useEffect, useRef, useState } from "react"
import Image from "next/image"
import { clsx } from "clsx"

import { Button, Drawer, Typography } from "@shared/ui.primitives"
import type { RelatedVariant } from "types"
import { CloseIcon, ChevronRightIcon } from "components/SVG"
import { dispatch } from "@shared/utils.events"

import * as styles from "./RelatedVariants.css"

type Props = {
  selectedVariantSlug: string
  relatedVariants: RelatedVariant[]
  title: string
}

export const RelatedVariants: React.FC<Props> = ({
  selectedVariantSlug,
  relatedVariants,
  title,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const modalRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (isOpen && modalRef.current) {
      modalRef.current.focus()
    }
  }, [isOpen])

  if (!relatedVariants || relatedVariants.length === 0) {
    return null
  }

  const selectedVariant = relatedVariants.find(
    (variant) => variant.slug === selectedVariantSlug
  )

  if (!selectedVariant) return null

  const handleDismiss = () => {
    dispatch("page element clicked", {
      target: `Shade Selector Modal ${selectedVariant.title}`,
      details: "close",
      location: "Shade Selector Modal",
    })
    setIsOpen(false)
  }

  const onVariantClick = (
    variant: RelatedVariant,
    event: React.MouseEvent | React.KeyboardEvent
  ) => {
    if (("key" in event && event.key !== "Enter") || event.type !== "click")
      return

    dispatch("page element clicked", {
      target: `Shade Selector Modal ${selectedVariant.title} to ${variant.title}`,
      contentUrl: variant?.url || "",
      location: "Shade Selector Modal",
    })
  }

  const showModal = () => {
    dispatch("page element clicked", {
      target: `Shade Selector Modal ${selectedVariant.title}`,
      details: "open",
      location: "PDP",
    })
    setIsOpen(true)
  }

  return (
    <>
      <div className={styles.container}>
        <Typography as="h6" variant="subheader-6" colour="primary-medium">
          {`${title} (${relatedVariants.length})`}
        </Typography>

        <button className={styles.button} onClick={showModal}>
          <div className={styles.buttonContent}>
            <Image
              src={selectedVariant.shadeVisual || "/images/placeholder.svg"}
              alt={selectedVariant.title}
              width={24}
              height={24}
            />
            <Typography as="p" variant="body-md">
              {selectedVariant.title}
            </Typography>
          </div>
          <ChevronRightIcon width={20} height={20} />
        </button>
      </div>

      <Drawer
        isOpen={isOpen}
        handleDismiss={handleDismiss}
        ariaDescription="See more shade options for this product"
        ariaLabel="Shade Selector"
        id="shade-selector-drawer"
        testId="shade-selector-drawer"
        headerId="shade-selector-drawer-header"
      >
        <Drawer.Header
          id="shade-selector-drawer-header"
          handleDismiss={handleDismiss}
        >
          {title}
        </Drawer.Header>
        <Drawer.Body>
          <ul className={styles.list}>
            {relatedVariants.map((item) => {
              const isActive = selectedVariantSlug === item.slug
              const isOutOfStock = item.availability?.status === "outofstock"
              return (
                <li key={item.id}>
                  <a
                    href={item.url}
                    title={item.title}
                    className={clsx(
                      styles.link,
                      isActive && styles.activeLink,
                      isOutOfStock && styles.oosLink
                    )}
                    onClick={(event) => onVariantClick(item, event)}
                    onKeyDown={(event) => onVariantClick(item, event)}
                  >
                    <div className={styles.linkContent}>
                      <Image
                        src={item.shadeVisual || "/images/placeholder.svg"}
                        alt={item.title}
                        width={45}
                        height={45}
                      />
                      {isOutOfStock && (
                        <CloseIcon
                          width="45px"
                          className={styles.outOfStockIcon}
                        />
                      )}
                      <Typography as="p" variant="body-md">
                        {item.title}
                      </Typography>
                    </div>
                  </a>
                </li>
              )
            })}
          </ul>
        </Drawer.Body>
        <Drawer.Footer>
          <Button
            width="full"
            variant="secondary"
            onClick={handleDismiss}
            text="Cancel"
          />
        </Drawer.Footer>
      </Drawer>
    </>
  )
}
